import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Button, DateNavigator, FieldChangeEvent, styles, useFormField } from '@frontend/design-system';
import { getMaxCalendarDate } from '../../../../../utils';

type TrackingIds = {
  todayButton?: string;
  prevButton?: string;
  nextButton?: string;
  dateInput?: string;
  datePicker?: string;
};

type Props = {
  showTodayButton?: boolean;
  selectedDate: string; // should be in 'MM/DD/YYYY' format
  todayDate: string; // should be in 'MM/DD/YYYY' format
  trackingIds?: TrackingIds;
  onSelectedDateChange: (value: string) => void;
};

export const CalendarDateSelectorUI = ({
  showTodayButton = true,
  trackingIds,
  selectedDate,
  todayDate,
  onSelectedDateChange,
}: Props) => {
  const { t } = useTranslation('schedule');

  const dateField = useFormField(
    {
      type: 'datePicker',
      required: true,
      minDate: dayjs().subtract(50, 'year').startOf('year').format('YYYY-MM-DD'),
      maxDate: getMaxCalendarDate('YYYY-MM-DD'),
      value: selectedDate,
    },
    [selectedDate]
  );

  const isTodayButtonDisabled = !dateField.error && selectedDate === todayDate;

  const handleDateChange = (event?: FieldChangeEvent) => {
    if (typeof event !== 'object') return;

    // Change event from input field. We will handle input field change on blur
    if ('target' in event && event.target && 'value' in event.target) {
      return;
    }

    // Change event from calendar picker
    if ('value' in event) {
      dateField.onChange(event);
      const value = event.value;
      if (dayjs(value).isValid()) {
        onSelectedDateChange(value);
      }
    }
  };

  return (
    <>
      {showTodayButton && (
        <Button
          variant='secondary'
          size='small'
          disabled={isTodayButtonDisabled}
          trackingId={trackingIds?.todayButton}
          onClick={() => onSelectedDateChange(todayDate)}
        >
          {t('Today')}
        </Button>
      )}
      <div css={[{ flex: 1 }, styles.flexCenter]}>
        <DateNavigator
          {...dateField}
          name='calendar-date'
          label=''
          showYear
          onChange={handleDateChange}
          trackingIds={{
            prevButton: trackingIds?.prevButton,
            nextButton: trackingIds?.nextButton,
            dateInput: trackingIds?.dateInput,
            datePicker: trackingIds?.datePicker,
          }}
        />
      </div>
    </>
  );
};
